import { type AiTagId } from '@solinkcorp/api-types';
import { useUserSession } from '@solinkcorp/authentication';
import { useCallhomeClient } from '@solinkcorp/callhome-data';
import { useCallback } from 'react';
import { mutate } from 'swr';
import useAiTags, { useCustomerAiTags } from './useAiTags';
import { AI_TAGS_KEY } from './utils';

/**
 * Returns a function that persists any selected AI tag ids that are not yet
 * saved as customer tags (i.e. not in /api/ai-tags).
 *
 * "Needs persisting" is decided by membership against useCustomerAiTags — covers
 * both default tags (selected by the user from clientSettings) and freshly-typed
 * tags. The tag name comes from useAiTags' merged list; ids that don't appear
 * there (e.g. a typed-in tag whose synthetic id IS the name) fall back to using
 * the id string as the name.
 *
 * Returns a Map<syntheticId, realId> the caller applies to the entity's tagIds
 * (widget.tagIds, line.selectedAiTags, zone.aiTagIds) via `remapAiTagIds`.
 */
export default function useSaveAiTags() {
  const user = useUserSession();
  const callhomeClient = useCallhomeClient();
  const { data: customerSavedTags } = useCustomerAiTags();
  const { data: allTags } = useAiTags();

  return useCallback(
    async (selectedTagIds: readonly AiTagId[]): Promise<Map<AiTagId, AiTagId>> => {
      const customerIds = new Set((customerSavedTags ?? []).map((t) => t.id));
      const idsToCreate = Array.from(new Set(selectedTagIds.filter((id) => !customerIds.has(id))));

      if (idsToCreate.length === 0) return new Map();

      const nameById = new Map(allTags.map((t) => [t.id, t.name] as const));

      const results = await Promise.all(
        idsToCreate.map(async (syntheticId) => {
          const name = nameById.get(syntheticId) ?? syntheticId;
          const saved = await callhomeClient.postAiTag({
            id: syntheticId,
            name,
            cloudId: user.jwtUser.app_metadata.cloudId,
            resellerId: user.jwtUser.app_metadata.resellerId,
            customerId: user.customerId,
          });
          return { syntheticId, saved };
        }),
      );

      const idRemap = new Map<AiTagId, AiTagId>();
      for (const { syntheticId, saved } of results) {
        if (saved.id) idRemap.set(syntheticId, saved.id);
      }

      await mutate(AI_TAGS_KEY);
      return idRemap;
    },
    [user, callhomeClient, customerSavedTags, allTags],
  );
}
